import React, { useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { AppContext } from '../../AppContext';
import Loading from '../Loading';
import SearchResultColumns from './SearchResultColumns';
import SearchResultsConsolidated from './SearchResultsConsolidated';
import SearchResultsNew from './SearchResultsNew';
import SearchBarLeftSide from '../searchbars/SearchBarLeftSide';
import SearchLogic from '../searchbars/SearchLogic';

const VIEW_MODES = {
	split: 'split',
	consolidated: 'consolidated',
	new: 'new',
};

// Helper functions for calculating accurate consolidated results count
function normalizeSources(part) {
	if (!part) {
		return [];
	}

	const rawSources = part.data_sources ?? part.data_source ?? [];

	if (Array.isArray(rawSources)) {
		return rawSources.map((source) => (source || '').toString().toLowerCase());
	}

	if (typeof rawSources === 'string') {
		return [rawSources.toLowerCase()];
	}

	return [];
}

function partHasRenderableContent(part) {
	if (!part || typeof part !== 'object') {
		return false;
	}

	const { mfgstockcode, stockcode, optimizeddescription, description, imageurl } = part;

	if (
		!((mfgstockcode && mfgstockcode.trim() !== '') ||
		(stockcode && stockcode.trim() !== '') ||
		(optimizeddescription && optimizeddescription.trim() !== '') ||
		(description && description.trim() !== ''))
	) {
		return false;
	}

	const sources = normalizeSources(part);

	return sources.length > 0 || Boolean(imageurl);
}

function dedupeParts(parts) {
	const seen = new Map();

	parts.forEach((part) => {
		const key = (part?.mfgstockcode || part?.stockcode || '').toLowerCase();
		if (!key) {
			return;
		}

		if (!seen.has(key)) {
			seen.set(key, { ...part });
			return;
		}

		const existing = seen.get(key);
		const currentSources = new Set(normalizeSources(existing));

		normalizeSources(part).forEach((source) => currentSources.add(source));
		existing.data_sources = Array.from(currentSources);

		if (!existing.imageurl && part.imageurl) {
			existing.imageurl = part.imageurl;
		}
		if ((!existing.optimizeddescription || existing.optimizeddescription === existing.description) && part.optimizeddescription) {
			existing.optimizeddescription = part.optimizeddescription;
		}
		if (!existing.description && part.description) {
			existing.description = part.description;
		}
		if (!existing.notes && part.notes) {
			existing.notes = part.notes;
		}
	});

	return Array.from(seen.values());
}

function SearchResults ({
	serNum,
	modNum,
	manName,
	refineQuery,
	setManName,
	setSerNum,
	setModNum,
	setRefineQuery,
	handleSearch
}) {
	const { isLoading, keepSRBlank, resultsViewMode, setResultsViewMode, searchResultsPartsAll, isConsolidatedLoading, searchResultsPartsSNL, searchResultsPartsWebsite, searchResultsPartsRC, searchResultsPartsParsed } = useContext(AppContext);

	const handleViewSwitch = (mode) => {
		setResultsViewMode(mode);
	};

	//Params from the URL
	// urlMfgName = Manufacturer Name
	// urlSN = Serial Number
	// urlMN = Model Number
	// urlRQ = Refine Query (phrase used to search for parts)
	const { urlMfgName, urlSN, urlMN, urlRQ } = useParams();

	// Cleans up the passed in parameters with the split function since
	// man=, mn_sn=, and query= are passed in the URL as well
	// Does try/catch to decode the URI if it's encoded and if it's not, just renders the raw value
	useEffect(() => {
		try {
			setManName(decodeURIComponent(urlMfgName.split("=")[1]));
		} catch {
			setManName(urlMfgName.split("=")[1]);
		}

		try {
			setSerNum(decodeURIComponent(urlSN.split("=")[1]));
		} catch {
			setSerNum(urlSN.split("=")[1]);
		}

		try {
			setModNum(decodeURIComponent(urlMN.split("=")[1]));
		} catch {
			setModNum(urlMN.split("=")[1]);
		}

		try {
			setRefineQuery(decodeURIComponent(urlRQ.split("=")[1]));
		} catch {
			setRefineQuery(urlRQ.split("=")[1]);
		}
	}, [urlMfgName, urlSN, urlMN, urlRQ, setManName, setSerNum, setModNum, setRefineQuery]);

	useEffect(() => {
		if (serNum || modNum || manName || refineQuery) {
			handleSearch();
		}
		}, [serNum, modNum, manName, refineQuery]);
		return (
			<div className={`w-full ${(resultsViewMode === VIEW_MODES.consolidated || resultsViewMode === VIEW_MODES.new) ? '-mt-3 lg:-mt-5' : ''}`}>
				{/* Loading component shows for old view only */}
				{(isLoading > 0 && resultsViewMode === VIEW_MODES.split) && <Loading />}

				{/* View switcher - always visible, persists across all views */}
				<div className='mb-4 flex justify-between items-center'>
					{/* Results count for consolidated and new views, empty div for split view */}
					{resultsViewMode === VIEW_MODES.consolidated ? (
						<div className='text-sm text-zinc-500 dark:text-zinc-400'>
							{(() => {
								const consolidatedData = searchResultsPartsAll && typeof searchResultsPartsAll === 'object' && !Array.isArray(searchResultsPartsAll)
									? searchResultsPartsAll
									: {};

								// Calculate count the same way SearchResultsConsolidated displays parts
								const regularParts = Array.isArray(consolidatedData.parts)
									? dedupeParts(consolidatedData.parts.filter(partHasRenderableContent))
									: [];

								const BLOOMFIRE_SENTINELS = new Set(['bloomfire', 'business_logic']);
								const hasBloomfirePart = consolidatedData?.bf_links &&
									partHasRenderableContent({
										...consolidatedData.bf_links,
										imageurl: consolidatedData.bf_links.imageurl || 'BLOOMFIRE',
										obsolete: consolidatedData.bf_links.obsolete || 'BLOOMFIRE',
									});

								const partsCount = regularParts.length + (hasBloomfirePart ? 1 : 0);
								return partsCount > 0 ? `${partsCount} result${partsCount === 1 ? '' : 's'}` : 'No consolidated results yet';
							})()}
						</div>
					) : resultsViewMode === VIEW_MODES.new ? (
						<div className='text-sm text-zinc-500 dark:text-zinc-400'>
							{(() => {
								// Calculate count from merged split data sources
								const seen = new Set();
								let count = 0;

								const addParts = (partsData) => {
									if (!partsData || typeof partsData !== 'object') return;

									let partsArray = [];
									if (partsData.parts) {
										// RC data structure
										Object.values(partsData.parts).forEach(modelParts => {
											if (Array.isArray(modelParts)) partsArray = partsArray.concat(modelParts);
										});
									} else {
										// SNL, Website, Parsed structure
										Object.values(partsData).forEach(modelParts => {
											if (Array.isArray(modelParts)) partsArray = partsArray.concat(modelParts);
										});
									}

									partsArray.forEach(part => {
										const key = (part?.mfgstockcode || part?.stockcode || '').toLowerCase();
										if (key && !seen.has(key)) {
											seen.add(key);
											count++;
										}
									});
								};

								addParts(searchResultsPartsSNL);
								addParts(searchResultsPartsWebsite);
								addParts(searchResultsPartsRC);
								addParts(searchResultsPartsParsed);

								return count > 0 ? `${count} result${count === 1 ? '' : 's'}` : 'No results yet';
							})()}
						</div>
					) : (
						<div></div>
					)}

					<div className='self-stretch inline-flex justify-start items-center gap-5'>
						<div className='flex justify-start items-center gap-2'>
							<div className='inline-flex flex-col justify-start items-start'>
								<div className="justify-center text-neutral-500 text-sm font-sf-pro leading-none">View:</div>
							</div>
						</div>
						<div className='relative p-0.5 bg-zinc-300 rounded-[10px] flex justify-start items-center gap-0.5'>
							{/* Sliding white background */}
							<div
								className={`absolute w-20 h-8 px-2 py-1 bg-white rounded-lg shadow-[0px_0px_10.699999809265137px_0px_rgba(0,0,0,0.25)] flex justify-center items-center gap-2 transition-transform duration-200 ease-in-out ${
									resultsViewMode === VIEW_MODES.consolidated ? 'translate-x-0' :
									resultsViewMode === VIEW_MODES.new ? 'translate-x-[calc(80px+2px)]' :
									'translate-x-[calc(160px+4px)]'
								}`}
							/>
							<button
								type='button'
								onClick={() => handleViewSwitch(VIEW_MODES.consolidated)}
								className='relative z-10 w-20 h-8 px-2 py-1 rounded-lg flex justify-center items-center gap-2'
								aria-pressed={resultsViewMode === VIEW_MODES.consolidated}
							>
								<div className='inline-flex flex-col justify-start items-start'>
									<div className="justify-center text-zinc-700 text-sm font-sf-pro leading-none">Improved</div>
								</div>
							</button>
							<button
								type='button'
								onClick={() => handleViewSwitch(VIEW_MODES.new)}
								className='relative z-10 w-20 h-8 px-2 py-1 rounded-lg flex justify-center items-center gap-2'
								aria-pressed={resultsViewMode === VIEW_MODES.new}
							>
								<div className='inline-flex flex-col justify-start items-start'>
									<div className="justify-center text-zinc-700 text-sm font-sf-pro leading-none">New</div>
								</div>
							</button>
							<button
								type='button'
								onClick={() => handleViewSwitch(VIEW_MODES.split)}
								className='relative z-10 w-20 h-8 px-2 py-1 rounded-lg flex justify-center items-center gap-2'
								aria-pressed={resultsViewMode === VIEW_MODES.split}
							>
								<div className='inline-flex flex-col justify-start items-start'>
									<div className="justify-center text-zinc-700 text-sm font-sf-pro leading-none">Old</div>
								</div>
							</button>
						</div>
					</div>
				</div>

				{/* Show view switcher and search bar for consolidated view even when loading or blank */}
				{resultsViewMode === VIEW_MODES.consolidated && (
					<div className='flex gap-6'>
						<div className='sticky top-0 self-start'>
							<SearchLogic>
								{props => <SearchBarLeftSide {...props} />}
							</SearchLogic>
						</div>
						<div className='flex-1'>
							{/* Show loading in the results area for both initial loading and consolidated loading */}
							{(isLoading > 0 || isConsolidatedLoading) ? (
								<Loading />
							) : !keepSRBlank ? (
								<SearchResultsConsolidated />
							) : null}
						</div>
					</div>
				)}

				{/* New view - combines old data sources with improved layout */}
				{resultsViewMode === VIEW_MODES.new && (
					<div className='flex gap-6'>
						<div className='sticky top-0 self-start'>
							<SearchLogic>
								{props => <SearchBarLeftSide {...props} />}
							</SearchLogic>
						</div>
						<div className='flex-1'>
							{/* Show loading in the results area for initial loading */}
							{isLoading > 0 ? (
								<Loading />
							) : !keepSRBlank ? (
								<SearchResultsNew />
							) : null}
						</div>
					</div>
				)}

				{/* Old split view - show only when not loading and not blank */}
				{resultsViewMode === VIEW_MODES.split && isLoading === 0 && !keepSRBlank && (
					<SearchResultColumns
						urlMfgName={manName}
						urlSN={serNum}
						urlMN={modNum}
						urlRQ={refineQuery}
					/>
				)}
			</div>
		)
	};

	export default SearchResults;
